import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load cart from localStorage for guests, or API for logged users
  const fetchCart = useCallback(async () => {
    if (user && token) {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/cart`);
        setItems(response.data.items || []);
      } catch (error) {
        console.error('Error fetching cart:', error);
      } finally {
        setLoading(false);
      }
    } else {
      const savedCart = localStorage.getItem('vivid-cart');
      if (savedCart) {
        setItems(JSON.parse(savedCart));
      }
    }
  }, [user, token]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Save cart to localStorage for guests
  useEffect(() => {
    if (!user) {
      localStorage.setItem('vivid-cart', JSON.stringify(items));
    }
  }, [items, user]);

  const addToCart = async (product, quantity = 1) => {
    if (user && token) {
      try {
        await axios.post(`${API}/cart/add`, { product_id: product.id, quantity });
        await fetchCart();
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    } else {
      setItems(prev => {
        const existing = prev.find(item => item.product.id === product.id);
        if (existing) {
          return prev.map(item =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        }
        return [...prev, { product, quantity }];
      });
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (user && token) {
      try {
        if (quantity <= 0) {
          await axios.delete(`${API}/cart/remove/${productId}`);
        } else {
          await axios.put(`${API}/cart/update`, { product_id: productId, quantity });
        }
        await fetchCart();
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    } else {
      if (quantity <= 0) {
        setItems(prev => prev.filter(item => item.product.id !== productId));
      } else {
        setItems(prev =>
          prev.map(item =>
            item.product.id === productId ? { ...item, quantity } : item
          )
        );
      }
    }
  };

  const removeFromCart = async (productId) => {
    if (user && token) {
      try {
        await axios.delete(`${API}/cart/remove/${productId}`);
        await fetchCart();
      } catch (error) {
        console.error('Error removing from cart:', error);
      }
    } else {
      setItems(prev => prev.filter(item => item.product.id !== productId));
    }
  };

  const clearCart = async () => {
    if (user && token) {
      try {
        await axios.delete(`${API}/cart/clear`);
        setItems([]);
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    } else {
      setItems([]);
      localStorage.removeItem('vivid-cart');
    }
  };

  const getCartTotal = () => {
    return items.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      items,
      loading,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      getCartTotal,
      getCartCount,
      refreshCart: fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
